import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { TagSummary } from "@media-viewer/shared";

interface TagPickerProps {
  mediaId: number;
  current: TagSummary[];
}

export function TagPicker({ mediaId, current }: TagPickerProps) {
  const qc = useQueryClient();
  const [input, setInput] = useState("");
  const allTags = useQuery({ queryKey: ["tags"], queryFn: api.tags });

  const remove = useMutation({
    mutationFn: ({ tagId }: { tagId: number }) => api.removeTag(mediaId, tagId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", mediaId] });
      qc.invalidateQueries({ queryKey: ["tags"] });
    },
  });
  const addExisting = useMutation({
    mutationFn: ({ tagId }: { tagId: number }) => api.addTags(mediaId, [tagId]),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", mediaId] });
      qc.invalidateQueries({ queryKey: ["tags"] });
    },
  });
  const createAndAdd = useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      const tag = await api.createTag(name);
      await api.addTags(mediaId, [tag.id]);
      return tag;
    },
    onSuccess: () => {
      setInput("");
      qc.invalidateQueries({ queryKey: ["media", mediaId] });
      qc.invalidateQueries({ queryKey: ["tags"] });
    },
  });

  const currentIds = new Set(current.map((t) => t.id));
  const trimmed = input.trim().toLowerCase();
  // Show every available tag the user can click — no top-N cap. The
  // previous 24-tag cutoff forced the user to start typing to surface
  // less-popular tags, which was a real friction point for "I want to
  // pick a known tag I just don't remember the exact name of". The
  // scrollable container caps vertical height instead, so the page
  // layout stays sane even on libraries with hundreds of tags.
  //
  // Order: most-used first (so the tags this library actually leans on
  // appear up top), then alphabetical as a tiebreaker.
  const available = (allTags.data ?? [])
    .filter((t) => !currentIds.has(t.id))
    .filter((t) => (trimmed ? t.name.toLowerCase().includes(trimmed) : true))
    .sort((a, b) => (b.count ?? 0) - (a.count ?? 0) || a.name.localeCompare(b.name));
  const suggestions = available;

  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-1">
        {current.length === 0 && <span className="text-xs text-zinc-500">No tags yet.</span>}
        {current.map((t) => (
          <span
            key={t.id}
            className="inline-flex items-center gap-1 rounded bg-zinc-700 px-2 py-0.5 text-xs"
          >
            {t.name}
            <button
              onClick={() => remove.mutate({ tagId: t.id })}
              className="text-zinc-400 hover:text-zinc-100"
              aria-label={`Remove ${t.name}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && input.trim().length > 0) {
              const exact = (allTags.data ?? []).find(
                (t) => t.name.toLowerCase() === input.trim().toLowerCase(),
              );
              if (exact) addExisting.mutate({ tagId: exact.id });
              else createAndAdd.mutate({ name: input.trim() });
            }
          }}
          placeholder={available.length > 0 ? "Type to filter or add new…" : "Add tag…"}
          className="w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1 text-sm focus:border-zinc-500 focus:outline-none"
        />
        {/* Always-visible chip list of every available tag. Click to add;
            type into the input to filter. Scrolls vertically when the
            list is long so the rest of the detail page stays in view.
            Helps avoid creating duplicate near-name tags ("dog", "Dogs",
            "doggos"). */}
        {suggestions.length > 0 && (
          <div className="mt-2 flex max-h-48 flex-wrap gap-1 overflow-y-auto rounded border border-zinc-800 bg-zinc-900/40 p-1">
            {suggestions.map((t) => (
              <button
                key={t.id}
                onClick={() => {
                  addExisting.mutate({ tagId: t.id });
                  setInput("");
                }}
                className="rounded border border-zinc-700 bg-zinc-800 px-2 py-0.5 text-xs text-zinc-300 hover:border-emerald-600 hover:bg-zinc-700"
                title={t.count != null ? `Applied to ${t.count} item${t.count === 1 ? "" : "s"}` : undefined}
              >
                + {t.name}
                {t.count != null && t.count > 0 && (
                  <span className="ml-1 text-zinc-500">{t.count}</span>
                )}
              </button>
            ))}
          </div>
        )}
        {trimmed && suggestions.length === 0 && (
          <div className="mt-2 text-xs text-zinc-500">
            No existing tag matches "{input.trim()}". Press Enter to create it.
          </div>
        )}
      </div>
    </div>
  );
}
